import { useRouter } from 'next/router';
import { useCallback, useContext, useMemo } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { KYCContext } from '~/components/providers/KYCProvider';
import {
  getUseLocalStorageSettingsKey,
  useLocalStorageSettings,
} from '~/domains/common/hooks/useLocalStorageSettings';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { NEW_ENTRY_ID } from '~/domains/contest/domains/common/constants';
import { SocureKycStatus } from '~/services/users/types';

import { MakePick } from '../components/MakePicksModal/enums';

function useAnonymousPicksheet<T>(fullContest: FullContest) {
  const router = useRouter();
  const { contestUuid, slateId, entryId } = router.query;

  const { user } = useContext(UserContext);
  const { KYCStatus } = useContext(KYCContext);
  const { data: balance, isLoading: isBalanceLoading } = useWalletBalance();
  // TODO: find a better way to check if we are in gameshell
  const isInGameshell = Boolean(contestUuid) && Boolean(slateId);

  const key = `picksheet::${contestUuid}::${slateId}`;
  const [value, setValue] = useLocalStorageSettings<T>(key, [] as T);

  const getValue = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem(getUseLocalStorageSettingsKey(key)));
    } catch (error) {
      console.error(error);
      return value;
    }
  }, [key, value]);

  const isAnonymousPicksheetEnabledCondition = useMemo(
    () => ({
      [MakePick.ANONYMOUS]: !entryId && !user,
      [MakePick.UNVERIFIED]: !entryId && KYCStatus !== SocureKycStatus.ACCEPT,
      [MakePick.DEPOSIT]:
        !entryId &&
        !isBalanceLoading &&
        balance?.availableBalance < (fullContest?.entry_fee ?? 0) / 100,
      [MakePick.ADD_NEW_ENTRY]: !entryId || entryId === NEW_ENTRY_ID,
    }),
    [KYCStatus, balance, entryId, fullContest?.entry_fee, isBalanceLoading, user]
  );

  return {
    isAnonymousPicksheetEnabled:
      isInGameshell && Object.values(isAnonymousPicksheetEnabledCondition).some(Boolean),
    isAnonymousPicksheetEnabledCondition,
    value,
    setValue,
    getValue,
  };
}

export { useAnonymousPicksheet };
